import { useRef } from 'react';
import { motion, useReducedMotion, useScroll } from 'framer-motion';
import { ArrowRightIcon, StarIcon } from 'lucide-react';
import { MediaView } from '../shared/MediaView';
import { HeroDoors } from './HeroDoors';
import { HeroFloatingProducts } from './HeroFloatingProducts';
import { founder, pressLogos } from '../../data/content';
import { policyCopy } from '../../data/policies';
import { usePages } from '../../contexts/SiteContext';
import { useOverlayActions } from '../../contexts/StoreContext';
import { useCompactMotion, useLayerValue, useLayerY } from '../../hooks/useParallax';
import { ease } from '../../core/format/motion';
import { plural } from '../../core/format/text';
import { mediaOr } from '../../core/rules/media';
import { fill } from '../../utils/copy';
import type { Product } from '../../core/types/catalog';
type Props = {
  reviewCount: number;
  avgRating: string;
  featured: Product[];
};

/** Wanneer de kopregels beginnen, en hoeveel later elke volgende. */
const LINE_DELAY_S = 0.08;
const LINE_STEP_S = 0.07;

/**
 * De opening (docs/VORMGEVING.md §3). Eén boodschap: salon én thuis, door één specialist.
 *
 * Computer: links de kop, de uitleg en één hoofdknop (het huidadvies); onderaan twee "deuren"
 * naar de salon en de webshop. Rechts het beeld tot de rand, met een onderschrift ín de foto.
 * Telefoon: het beeld bovenaan, daaronder kop, uitleg en knop. De deuren direct daaronder.
 *
 * Diepte in lagen, elk met een eigen snelheid bij scrollen (hooks/useParallax.ts):
 * - achter: het portret, het langzaamst, en het zoomt licht uit (106% → 100%);
 * - midden: de tekst scrolt gewoon mee en vervaagt als de hero half weg is;
 * - tussen: het kaartje met naam en ervaring;
 * - voor: twee bestsellers die over de rand van het portret zweven, het snelst.
 * Binnenkomst: het portret staat er meteen (grootste element), dan de tekst, dan de producten.
 * Alles stil bij "minder beweging"; op de telefoon de halve uitslag.
 */
export function HomeHero({
  reviewCount,
  avgRating,
  featured
}: Props) {
  const pages = usePages();
  const ref = useRef<HTMLElement>(null);
  const {
    openQuiz
  } = useOverlayActions();
  const reduce = useReducedMotion();
  const copy = pages.home.hero;
  const media = mediaOr(copy.media, {
    image: founder.portrait,
    alt: `${founder.name}, huidspecialist`,
    focus: {
      x: 50,
      y: 25
    }
  });
  const press = pressLogos[0]?.name;
  const afterLines = LINE_DELAY_S + copy.lines.length * LINE_STEP_S;
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start start', 'end start']
  });
  const portraitY = useLayerY(scrollYProgress, [0, 64]);
  const portraitScale = useLayerValue(scrollYProgress, [0, 1], [1.06, 1]);
  const captionY = useLayerY(scrollYProgress, [0, -56]);
  const productsY = useLayerY(scrollYProgress, [0, -150]);
  const textFade = useLayerValue(scrollYProgress, [0.45, 0.85], [1, 0]);
  // Vervagen alleen op de computer, waar de hero één scherm is. Op de telefoon staan kop, knop en
  // deuren onder het beeld: die lees je nog terwijl de hero wegscrolt.
  const compact = useCompactMotion();
  const fade = compact ? undefined : textFade;

  /** Zacht omhoog, na `delay` seconden. */
  const rise = (delay: number) => reduce ? {} : {
    initial: {
      opacity: 0,
      y: 8
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.26,
      delay,
      ease
    }
  };
  return <section ref={ref} className="bg-sand" aria-label="Introductie" data-id="element-7361" data-mp="2mqfh26i2wckt">
      <div className="grid lg:min-h-[min(calc(100svh-120px),54rem)] lg:grid-cols-2" data-id="element-7362" data-mp="3kde4rrqsma6k">
        {/* Beeld met zijn lagen: op de telefoon eerst, op de computer rechts tot de rand. */}
        <div className="relative lg:order-2" data-id="element-7363" data-mp="3an31m7exo50q">
          <div className="relative h-[40svh] min-h-[300px] max-h-[460px] overflow-hidden bg-blush sm:h-[52svh] sm:max-h-[560px] lg:h-full lg:max-h-none" data-id="element-7364" data-mp="3alry88pegza5">
            {/* Extra hoogte boven: het beeld zakt trager weg dan de pagina, zonder ooit een rand te tonen. */}
            <motion.div style={portraitY && portraitScale ? {
            y: portraitY,
            scale: portraitScale
          } : undefined} className="absolute inset-x-0 -top-[12%] bottom-0 lg:-top-[9%]" data-id="element-7365" data-mp="27j8d8e9imlo">
              <MediaView media={media} priority sizes="(min-width: 1024px) 50vw, 100vw" controlsPosition="top-right" className="h-full w-full" data-id="element-7366" data-mp="3mwhiolt72m1x" />
            </motion.div>

            {/* Het onderschrift ín de foto, als eigen laag: op een shell-vlak, dus leesbaar op elk beeld. */}
            <motion.div style={captionY ? {
            y: captionY
          } : undefined} className="absolute bottom-4 left-4 max-w-[calc(100%-2rem)] sm:bottom-6 sm:left-6 lg:bottom-10 lg:left-10" data-id="element-7367" data-mp="5f7tc3np5gir">
              <motion.div {...rise(0.25)} className="bg-shell px-4 py-3 sm:px-5 sm:py-4" data-id="element-7368" data-mp="3ruar7lhyzs5d">
                <p data-id="element-7369" data-mp="o99iu3i20ar">
                  <span className="block font-display text-[1.15rem] leading-tight text-ink sm:text-[1.3rem]" data-id="element-7370" data-mp="nq247bh6uaz">{founder.name}</span>
                  <span className="mt-0.5 block text-[13px] leading-snug text-muted" data-id="element-7371" data-mp="ociuxuwswm1d">
                    {founder.role} · {policyCopy.experience}
                  </span>
                  {(reviewCount > 0 || press) && <span className="mt-2.5 hidden items-center gap-3 border-t border-line pt-2.5 text-[13px] text-ink sm:flex" data-id="element-7372" data-mp="28ww80imkkpd2">
                      {reviewCount > 0 && <span className="inline-flex items-center gap-1.5 whitespace-nowrap" data-id="element-7373" data-mp="2mpvp2p202rmq">
                          <StarIcon className="h-3.5 w-3.5 fill-current text-clay" strokeWidth={0} aria-hidden="true" data-id="element-7374" data-mp="2iok4ixxzgwle" />
                          {avgRating}
                          <span className="sr-only" data-id="element-7375" data-mp="1pytqsq09jwpt"> van 5 sterren,</span>
                          <span className="text-muted" data-id="element-7376" data-mp="23y2votj0r4eh"> · {plural(reviewCount, 'review', 'reviews')}</span>
                        </span>}
                      {press && <span className="whitespace-nowrap text-muted" data-id="element-7377" data-mp="1z0t9rnuu9ave">Aanbevolen door {press}</span>}
                    </span>}
                </p>
              </motion.div>
            </motion.div>
          </div>

          {/* Voorlaag: buiten het bijgesneden beeld, zodat de producten over de rand mogen steken. */}
          <HeroFloatingProducts products={featured} y={productsY} delay={afterLines + 0.15} data-id="element-7378" data-mp="2cysk1wg2pmgl" />
        </div>

        {/* Tekst: de middenlaag. */}
        <motion.div style={fade ? {
        opacity: fade
      } : undefined} className="flex flex-col px-6 pb-12 pt-7 sm:px-10 sm:pt-10 lg:order-1 lg:py-14 lg:pl-[max(2.5rem,calc((100vw-80rem)/2+2.5rem))] lg:pr-24 xl:pr-28" data-id="element-7379" data-mp="2h1owt6ixmocm">
          <div className="lg:my-auto" data-id="element-7380" data-mp="1tb1m6dhmtkmd">
            <motion.h1 {...rise(0)} className="text-[14px] text-clay" data-id="element-7381" data-mp="1tbun2xtwtfwl">
              {fill(copy.eyebrow)}
            </motion.h1>
            <p className="mt-4 text-balance font-display text-[2.2rem] font-light leading-[1.04] tracking-[-0.02em] text-ink sm:text-[3rem] lg:mt-5 lg:text-[clamp(2.75rem,3.5vw,3.9rem)]" data-id="element-7382" data-mp="1y8l1lgq2g3fw">
              {copy.lines.map((line, i) => <span key={`${i}-${line.text}`} className="block overflow-hidden pb-[0.06em]" data-id="element-7383" data-mp="1akbod36x4o0d" data-mpi={i}>
                  <motion.span className={`block ${line.accent ? 'italic text-clay' : ''}`} initial={reduce ? false : {
                y: '105%'
              }} animate={{
                y: 0
              }} transition={{
                duration: 0.3,
                delay: LINE_DELAY_S + i * LINE_STEP_S,
                ease
              }} data-id="element-7384" data-mp="131u6a8kz80vs">
                    {fill(line.text)}
                  </motion.span>
                </span>)}
            </p>
            {copy.body && <motion.p {...rise(afterLines)} className="mt-5 max-w-[34rem] text-[16px] leading-relaxed text-muted sm:text-[17px] lg:mt-6" data-id="element-7385" data-mp="fbq66b6zyj9j">
                {fill(copy.body)}
              </motion.p>}
            {copy.cta && <motion.div {...rise(afterLines + 0.05)} className="mt-7 lg:mt-9" data-id="element-7386" data-mp="5eljma98ols7">
                <button type="button" onClick={openQuiz} aria-haspopup="dialog" className="group inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-full bg-ink px-7 text-[15px] text-shell transition-[background-color,transform] duration-150 ease-soft hover:bg-clay active:scale-[0.98] sm:w-auto" data-id="element-7387" data-mp="1c8adbvaa5qo">
                  {fill(copy.cta)}
                  <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-soft group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-7388" data-mp="1b8lsseb6miw3" />
                </button>
              </motion.div>}
          </div>

          <motion.div {...rise(afterLines + 0.1)} className="mt-10 lg:mt-12" data-id="element-7389" data-mp="16aamgdk3iqim">
            <HeroDoors data-id="element-7390" data-mp="rkwcp4cly72p" />
          </motion.div>
        </motion.div>
      </div>
    </section>;
}